import {useMutation, useQueryClient} from '@tanstack/react-query';
import extractBackendErrorDetail from '@/utils/extractBackendErrorDetail';
import {
  PostProduction,
  POST_PRODUCTION_GENERATION_STATUS,
  POST_PRODUCTION_GENERATION_TYPE,
} from '@/types/postProductionGeneration';
import postProductionGenerationUpdaters, {
  type StartResizeArgs,
} from '@/api/updaters/postProductionGeneration';
import {POST_PRODUCTION_QUERY_KEY} from '@/hooks/use-post-production-generations';

type CacheShape = {data: {postProduction: PostProduction | null}} | undefined;

function resizeErrorDetail(error: Error): string | undefined {
  const raw = extractBackendErrorDetail(error);
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.error === 'string') return parsed.error;
  } catch {
    return raw;
  }
  return raw;
}

interface UseStartResizeMutationParams {
  onSuccess?: () => void;
  onError?: (error: Error, detail?: string) => void;
}

function useStartResizeMutation({
  onSuccess,
  onError,
}: UseStartResizeMutationParams = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: StartResizeArgs) =>
      postProductionGenerationUpdaters.startResize(args),
    onMutate: async ({assetId, targetResolution}: StartResizeArgs) => {
      const queryKey = [POST_PRODUCTION_QUERY_KEY, assetId];
      await queryClient.cancelQueries({queryKey});
      const previous = queryClient.getQueryData<CacheShape>(queryKey);

      queryClient.setQueryData<CacheShape>(queryKey, (old) => {
        const current = old?.data?.postProduction;
        // Nothing cached yet — don't fabricate remaining/limits; onSuccess refetches.
        if (!current) return old;

        return {
          data: {
            postProduction: {
              ...current,
              generations: [
                ...current.generations,
                {
                  id: `optimistic-${targetResolution}-${Date.now()}`,
                  generationType: POST_PRODUCTION_GENERATION_TYPE.RESIZE,
                  status: POST_PRODUCTION_GENERATION_STATUS.PENDING,
                  targetResolution,
                  url: null,
                  thumbnailUrl: null,
                  failureReason: null,
                },
              ],
              remaining: {
                ...current.remaining,
                resize: Math.max(0, current.remaining.resize - 1),
              },
            },
          },
        };
      });

      return {previous, queryKey};
    },
    onError: (
      error: Error,
      _args: StartResizeArgs,
      context?: {previous: CacheShape; queryKey: unknown[]}
    ) => {
      if (context) {
        queryClient.setQueryData(context.queryKey, context.previous);
      }
      onError?.(error, resizeErrorDetail(error));
    },
    onSuccess: (_data, {assetId}: StartResizeArgs) => {
      queryClient.invalidateQueries({
        queryKey: [POST_PRODUCTION_QUERY_KEY, assetId],
      });
      onSuccess?.();
    },
  });
}

export default useStartResizeMutation;
